import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import styled from "styled-components";

interface PropertyMapProps {
  lat: number;
  lng: number;
  zoom?: number;
  height?: number;
  // Fills 100% of the parent's height instead of a fixed pixel height, with
  // no bottom margin — for when the map itself IS the container (e.g. the
  // executive summary cockpit's map box), not a block stacked above other content.
  fill?: boolean;
}

const MapWrapper = styled.div<{ $height: number; $fill?: boolean }>`
  width: 100%;
  height: ${(p) => (p.$fill ? "100%" : `${p.$height}px`)};
  border-radius: 0.75rem;
  overflow: hidden;
  margin-bottom: ${(p) => (p.$fill ? 0 : p.theme.spacing.small)};
  position: relative;
  z-index: 0;

  .leaflet-container {
    width: 100%;
    height: 100%;
  }

  .leaflet-control-zoom,
  .leaflet-control-attribution {
    display: none;
  }
`;

// Covers the blank canvas Leaflet leaves behind when no tile ever loads, with
// the same line the cockpit shows when there is no point to draw at all.
const TileFailureNotice = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  text-align: center;
  background: #f4f6f5;
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #6b7f88;
`;

export const PropertyMap = ({ lat, lng, zoom = 18, height = 200, fill = false }: PropertyMapProps) => {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const [tilesFailed, setTilesFailed] = useState(false);

  useEffect(() => {
    if (!mapRef.current) return;

    // Cleanup previous instance if coordinates changed
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    const map = L.map(mapRef.current, {
      zoomControl: false,
      attributionControl: false,
      dragging: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      touchZoom: false,
      boxZoom: false,
      keyboard: false,
    }).setView([lat, lng], zoom);

    // The tile server can be down or blocked, and Leaflet says nothing about it:
    // it just leaves the canvas grey under the marker. Count what actually
    // arrives and only call it a failure when the layer settles with nothing
    // drawn — single tiles 404 at some zooms without the map being broken.
    const tiles = L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png");
    let loadedTiles = 0;
    tiles.on("tileload", () => {
      loadedTiles += 1;
      setTilesFailed(false);
    });
    tiles.on("load", () => setTilesFailed(loadedTiles === 0));
    tiles.addTo(map);

    const icon = L.divIcon({
      html: '<div style="font-size:28px;text-align:center;line-height:1">📍</div>',
      iconSize: [28, 28],
      iconAnchor: [14, 28],
      className: "",
    });

    L.marker([lat, lng], { icon }).addTo(map);

    mapInstanceRef.current = map;

    // Force Leaflet to recalculate size after render
    setTimeout(() => map.invalidateSize(), 100);

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, [lat, lng, zoom]);

  return (
    <MapWrapper $height={height} $fill={fill}>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      {tilesFailed && (
        <TileFailureNotice>
          {t("dueDiligence.finalReport.cockpit.mapUnavailable")}
        </TileFailureNotice>
      )}
    </MapWrapper>
  );
};
